// DAS-2107: "Your Institutional Trend" modal for PortfolioAnalysisView
// Three sections:
//   1. Likely Entry / Exit signals
//   2. Highest & lowest heldPercentInstitutions
//   3. Highest held_institution_delta_pct (recent institutional change)

import React, { useMemo } from 'react';
import {
  Box, Typography, Paper, Chip, Divider,
  Table as MuiTable, TableHead, TableBody, TableRow, TableCell,
  TableContainer,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ClickableTickerChip from './ClickableTickerChip';

const pct = (v, decimals = 2) => {
  const n = parseFloat(v);
  if (v == null || isNaN(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(decimals)}%`;
};

const fmtPct = (v) => {
  const n = parseFloat(v);
  if (v == null || isNaN(n)) return '—';
  return `${n.toFixed(1)}%`;
};

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{ mt: 3, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}
  >
    {children}
  </Typography>
);

const SignalRow = ({ ticker, company, direction, conviction, text, source }) => {
  const isEntry = direction === 'entry';
  const color = isEntry ? '#2e7d32' : '#c62828';
  const Icon = isEntry ? TrendingUpIcon : TrendingDownIcon;
  const label = isEntry ? 'Likely Entry' : 'Likely Exit';

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={2}
      sx={{
        py: 1.5, px: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ width: 4, minHeight: 40, bgcolor: color, borderRadius: 1, flexShrink: 0, mt: 0.5 }} />
      <Icon sx={{ color, mt: 0.3, fontSize: 20 }} />
      <Box flex={1} minWidth={0}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <ClickableTickerChip ticker={ticker} source={source} />
          <Chip label={label} size="small" sx={{ bgcolor: `${color}14`, color, fontWeight: 600, fontSize: '0.65rem', height: 22 }} />
          {conviction != null && (
            <Typography variant="caption" color="text.secondary">
              Conviction: {conviction}
            </Typography>
          )}
        </Box>
        {company && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            {company}
          </Typography>
        )}
        {text && (
          <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5, fontStyle: 'italic', whiteSpace: 'pre-line' }}>
            {text}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const PAModal_InstitutionalTrend = ({ tpData, yfData, data, source = 'portfolio' }) => {
  const merged = useMemo(() => {
    const all = [];
    const tickerSet = new Set([...Object.keys(tpData), ...Object.keys(yfData)]);

    tickerSet.forEach(ticker => {
      const tp = tpData[ticker] || {};
      const yf = yfData[ticker] || {};
      const position = data.find(d => (d.Ticker || '').toUpperCase() === ticker.toUpperCase());

      all.push({
        ticker,
        company: position?.Company || '',
        likelyEntry: tp.Likely_Institutional_Entry === 1,
        likelyExit: tp.Likely_Institutional_Exit === 1,
        institutionalText: tp.institutional_trend_text || '',
        convictionScore: tp.institutional_entry_conviction_score,
        convictionGrade: tp.institutional_entry_conviction_grade || '',
        deltaPct: parseFloat(tp.held_institution_delta_pct),
        heldPct: parseFloat(yf.heldPercentInstitutions),
        nInstitutes: yf.nInstitutes_holdingshares,
      });
    });

    return all;
  }, [tpData, yfData, data]);

  // Section 1: Entry / Exit signals
  const entries = useMemo(() => merged.filter(m => m.likelyEntry).sort((a, b) => (b.convictionScore || 0) - (a.convictionScore || 0)), [merged]);
  const exits = useMemo(() => merged.filter(m => m.likelyExit), [merged]);

  // Section 2: Highest & lowest held%
  const byHeldPct = useMemo(() => {
    const valid = merged.filter(m => !isNaN(m.heldPct));
    const sorted = [...valid].sort((a, b) => b.heldPct - a.heldPct);
    const top5 = sorted.slice(0, 5);
    const bottom5 = sorted.slice(-5).reverse();
    return { top5, bottom5 };
  }, [merged]);

  // Section 3: Highest delta%
  const byDeltaPct = useMemo(() => {
    const valid = merged.filter(m => !isNaN(m.deltaPct));
    return [...valid].sort((a, b) => b.deltaPct - a.deltaPct);
  }, [merged]);

  if (merged.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
        <Typography color="text.secondary">No institutional trend data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* --- Section 1: Likely Entry / Exit --- */}
      <SectionTitle>Institutional Entry & Exit Signals</SectionTitle>
      {entries.length === 0 && exits.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No entry or exit signals detected</Typography>
      ) : (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          {entries.map((e, i) => (
            <SignalRow
              key={`entry-${e.ticker}`}
              ticker={e.ticker}
              company={e.company}
              direction="entry"
              conviction={e.convictionGrade ? `${e.convictionGrade} (${e.convictionScore})` : e.convictionScore}
              text={e.institutionalText}
              source={source}
            />
          ))}
          {entries.length > 0 && exits.length > 0 && <Divider />}
          {exits.map((e, i) => (
            <SignalRow
              key={`exit-${e.ticker}`}
              ticker={e.ticker}
              company={e.company}
              direction="exit"
              conviction={null}
              text={e.institutionalText}
              source={source}
            />
          ))}
        </Paper>
      )}

      {/* --- Section 2: Highest / Lowest institutional ownership --- */}
      <SectionTitle>Institutional Ownership — Highest & Lowest</SectionTitle>
      <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
        {[
          { title: 'Highest Ownership', items: byHeldPct.top5, color: '#2e7d32' },
          { title: 'Lowest Ownership', items: byHeldPct.bottom5, color: '#c62828' },
        ].map(({ title, items, color }) => (
          <TableContainer key={title} component={Paper} variant="outlined" sx={{ flex: '1 1 320px', minWidth: 300 }}>
            <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5, color, fontSize: '0.75rem' }}>
              {title}
            </Typography>
            <MuiTable size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Ticker</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Held by Inst.</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}># Institutes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.ticker} hover>
                    <TableCell>
                      <ClickableTickerChip ticker={row.ticker} source={source} />
                      {row.company && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                          {row.company}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 500 }}>{fmtPct(row.heldPct)}</TableCell>
                    <TableCell align="right">
                      {row.nInstitutes != null ? Math.round(row.nInstitutes).toLocaleString() : '—'}
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography variant="caption" color="text.secondary">No data</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ))}
      </Box>

      {/* --- Section 3: Institutional position change (delta %) --- */}
      <SectionTitle>Recent Institutional Position Change</SectionTitle>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '40vh' }}>
        <MuiTable size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Ticker</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Delta %</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>Held by Inst.</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Signal</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {byDeltaPct.map((row) => {
              const deltaColor = !isNaN(row.deltaPct)
                ? (row.deltaPct > 0 ? '#2e7d32' : row.deltaPct < 0 ? '#c62828' : undefined)
                : undefined;

              return (
                <TableRow key={row.ticker} hover>
                  <TableCell>
                    <ClickableTickerChip ticker={row.ticker} source={source} />
                    {row.company && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                        {row.company}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: deltaColor }}>
                    {pct(row.deltaPct)}
                  </TableCell>
                  <TableCell align="right">{fmtPct(row.heldPct)}</TableCell>
                  <TableCell>
                    {row.likelyEntry && <Chip label="Entry" size="small" sx={{ bgcolor: '#2e7d3214', color: '#2e7d32', mr: 0.5, height: 20, fontSize: '0.65rem' }} />}
                    {row.likelyExit && <Chip label="Exit" size="small" sx={{ bgcolor: '#c6282814', color: '#c62828', height: 20, fontSize: '0.65rem' }} />}
                    {!row.likelyEntry && !row.likelyExit && '—'}
                  </TableCell>
                </TableRow>
              );
            })}
            {byDeltaPct.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="caption" color="text.secondary">No delta data available</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
    </Box>
  );
};

export default PAModal_InstitutionalTrend;
